import styled from 'styled-components';
import { Card, EmptyState, Eyebrow, StatusPill } from './ui';
import { sessionTitle } from '../lib/messages';
import type { OpencodeStatus, SelectedGlobalContext, Session } from '../types';

const Wrap = styled.div`
  display: grid;
  gap: 18px;
`;

const Hero = styled(Card)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 26px;
  overflow: hidden;
  background:
    radial-gradient(circle at top right, ${p => p.theme.primarySoft}, transparent 34%),
    ${p => p.theme.surface};

  @media (max-width: 760px) {
    align-items: flex-start;
    flex-direction: column;
    padding: 20px;
  }
`;

const HeroMain = styled.div`
  min-width: 0;
`;

const TitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
`;

const Title = styled.h2`
  margin: 0;
  color: ${p => p.theme.text};
  font-family: 'Raleway', system-ui, sans-serif;
  font-size: clamp(28px, 4vw, 42px);
  font-weight: 900;
  letter-spacing: -0.05em;
`;

const Description = styled.p`
  max-width: 760px;
  margin: 10px 0 0;
  color: ${p => p.theme.muted};
  font-size: 15px;
  line-height: 1.6;
`;

const HeroBadge = styled.div`
  flex: 0 0 auto;
  min-width: 170px;
  padding: 18px;
  border: 1px solid ${p => p.theme.border};
  border-radius: 18px;
  background: ${p => p.theme.surfaceStrong};
  box-shadow: ${p => p.theme.shadow};
`;

const BadgeLabel = styled.div`
  color: ${p => p.theme.muted};
  font-size: 12px;
  font-weight: 800;
`;

const BadgeValue = styled.div`
  margin-top: 6px;
  color: ${p => p.theme.text};
  font-size: 28px;
  font-weight: 900;
  letter-spacing: -0.04em;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const MetricCard = styled(Card)`
  padding: 18px;
`;

const MetricValue = styled.div`
  margin-top: 10px;
  color: ${p => p.theme.text};
  font-size: 26px;
  font-weight: 900;
  letter-spacing: -0.03em;
`;

const MetricSub = styled.div`
  margin-top: 4px;
  color: ${p => p.theme.muted};
  font-size: 12px;
  font-weight: 700;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, .95fr);
  gap: 18px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const DetailCard = styled(Card)`
  padding: 20px;
`;

const SectionTitle = styled.h3`
  margin: 6px 0 16px;
  color: ${p => p.theme.text};
  font-family: 'Raleway', system-ui, sans-serif;
  font-size: 20px;
  font-weight: 900;
  letter-spacing: -0.03em;
`;

const ContextList = styled.div`
  display: grid;
  gap: 10px;
`;

const ContextRow = styled.div`
  display: grid;
  grid-template-columns: 118px minmax(0, 1fr);
  gap: 14px;
  padding: 13px 14px;
  border: 1px solid ${p => p.theme.border};
  border-radius: 14px;
  background: ${p => p.theme.surfaceStrong};

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
    gap: 6px;
  }
`;

const RowLabel = styled.div`
  color: ${p => p.theme.primary};
  font-size: 12px;
  font-weight: 900;
  letter-spacing: .08em;
  text-transform: uppercase;
`;

const RowValue = styled.div`
  min-width: 0;
  color: ${p => p.theme.text};
  font-size: 14px;
  font-weight: 800;
`;

const RowMeta = styled.div`
  margin-top: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${p => p.theme.muted};
  font-size: 12px;
  font-weight: 600;
`;

const Insight = styled.div`
  padding: 16px;
  border: 1px solid ${p => p.theme.border};
  border-radius: 16px;
  background: ${p => p.theme.surfaceSoft};
  color: ${p => p.theme.muted};
  font-size: 14px;
  line-height: 1.55;
`;

const SessionList = styled.div`
  display: grid;
  gap: 10px;
`;

const SessionRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 13px 14px;
  border: 1px solid ${p => p.theme.border};
  border-radius: 14px;
  background: ${p => p.theme.surfaceStrong};
`;

const SessionText = styled.div`
  min-width: 0;
`;

const SessionName = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${p => p.theme.text};
  font-size: 14px;
  font-weight: 900;
`;

const SessionMeta = styled.div`
  margin-top: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${p => p.theme.muted};
  font-size: 12px;
`;

type Props = {
  context: SelectedGlobalContext | null;
  sessions: Session[];
  status: OpencodeStatus;
};

function compact(parts: Array<string | number | null | undefined>) {
  return parts.filter(part => part !== null && part !== undefined && `${part}`.trim()).join(' · ');
}

function titleForContext(context: SelectedGlobalContext | null) {
  if (!context) return 'Everything';
  if (context.user?.name) return context.user.name;
  if (context.user?.email) return context.user.email;
  if (context.workspace?.name) return context.workspace.name;
  if (context.company?.name) return context.company.name;
  return 'Selected context';
}

function scopeForContext(context: SelectedGlobalContext | null) {
  if (!context) return 'Everything';
  const company = Boolean(context.company);
  const workspace = Boolean(context.workspace);
  const user = Boolean(context.user);
  if (company && workspace && user) return 'Company + workspace + user';
  if (user && workspace) return 'User + workspace';
  if (user && company) return 'User + company';
  if (company && workspace) return 'Company + workspace';
  if (user) return 'User only';
  if (workspace) return 'Workspace only';
  if (company) return 'Company only';
  return 'Selected context';
}

function descriptionForContext(context: SelectedGlobalContext | null) {
  if (!context) return 'No context is selected, so this overview represents the whole available support workspace and all AI sessions.';
  if (context.user && context.workspace) return 'Focused on a specific user inside a workspace. AI Questions will inherit this exact context for support follow-ups.';
  if (context.user) return 'Focused on a specific user. Add a company or workspace when you want tighter operational context.';
  if (context.workspace) return 'Focused on one workspace. Add a user to move from workspace-level troubleshooting to person-level support.';
  if (context.company) return 'Focused on one company. Add a workspace or user to narrow the operational view.';
  return 'Overview follows the currently selected context.';
}

function sourcesForContext(context: SelectedGlobalContext | null) {
  const sources = context?.user?.sources || [];
  if (!sources.length) return 'Not scoped';
  return sources.map(source => source === 'metabase' ? 'Workspace' : source === 'pipedrive' ? 'Pipedrive' : 'PostHog').join(' + ');
}

function contextDepth(context: SelectedGlobalContext | null) {
  if (!context) return 0;
  return [context.company, context.workspace, context.user].filter(Boolean).length;
}

function contextRows(context: SelectedGlobalContext | null) {
  if (!context) return [];
  return [
    context.company ? {
      label: 'Company',
      value: context.company.name,
      meta: compact([`ID ${context.company.id}`, context.company.clientKey && `Client ${context.company.clientKey}`]),
    } : null,
    context.workspace ? {
      label: 'Workspace',
      value: context.workspace.name,
      meta: compact([`ID ${context.workspace.id}`, context.workspace.token && `Token ${context.workspace.token}`, context.workspace.ownerEmail]),
    } : null,
    context.user ? {
      label: 'User',
      value: context.user.name || context.user.email || context.user.emails?.[0] || 'User',
      meta: compact([context.user.email || context.user.emails?.[0], sourcesForContext(context)]),
    } : null,
  ].filter(Boolean) as Array<{ label: string; value: string; meta: string }>;
}

function sessionContextLine(session: Session) {
  return compact([
    session.context?.company?.name,
    session.context?.workspace?.name,
    session.context?.user?.email,
  ]) || 'No saved context';
}

export function OverviewPanel({ context, sessions, status }: Props) {
  const parents = sessions.filter(session => !session.parentID).length;
  const children = sessions.filter(session => session.parentID).length;
  const recentSessions = [...sessions].slice(-5).reverse();
  const rows = contextRows(context);

  return (
    <Wrap>
      <Hero>
        <HeroMain>
          <Eyebrow>Overview</Eyebrow>
          <TitleRow>
            <Title>{titleForContext(context)}</Title>
            <StatusPill $ok={Boolean(status.ok)}>{status.ok ? 'Online' : status.checked ? 'Offline' : 'Checking'}</StatusPill>
          </TitleRow>
          <Description>{descriptionForContext(context)}</Description>
        </HeroMain>
        <HeroBadge>
          <BadgeLabel>Current scope</BadgeLabel>
          <BadgeValue>{scopeForContext(context)}</BadgeValue>
        </HeroBadge>
      </Hero>

      <MetricsGrid>
        <MetricCard>
          <Eyebrow>Total sessions</Eyebrow>
          <MetricValue>{sessions.length}</MetricValue>
          <MetricSub>Across AI Questions</MetricSub>
        </MetricCard>
        <MetricCard>
          <Eyebrow>Primary / child</Eyebrow>
          <MetricValue>{parents} / {children}</MetricValue>
          <MetricSub>Conversation structure</MetricSub>
        </MetricCard>
        <MetricCard>
          <Eyebrow>Context cards</Eyebrow>
          <MetricValue>{contextDepth(context)} / 3</MetricValue>
          <MetricSub>{context ? 'Selected below toolbar' : 'No context selected'}</MetricSub>
        </MetricCard>
        <MetricCard>
          <Eyebrow>User sources</Eyebrow>
          <MetricValue>{sourcesForContext(context)}</MetricValue>
          <MetricSub>Based on selected user</MetricSub>
        </MetricCard>
      </MetricsGrid>

      <DetailGrid>
        <DetailCard>
          <Eyebrow>Selected data</Eyebrow>
          <SectionTitle>Context breakdown</SectionTitle>
          {rows.length > 0 ? (
            <ContextList>
              {rows.map(row => (
                <ContextRow key={row.label}>
                  <RowLabel>{row.label}</RowLabel>
                  <RowValue>
                    <div>{row.value}</div>
                    {row.meta && <RowMeta title={row.meta}>{row.meta}</RowMeta>}
                  </RowValue>
                </ContextRow>
              ))}
            </ContextList>
          ) : (
            <EmptyState>No context selected yet. Use the search in the top toolbar to focus the overview.</EmptyState>
          )}
        </DetailCard>

        <DetailCard>
          <Eyebrow>How this behaves</Eyebrow>
          <SectionTitle>Context-aware dashboard</SectionTitle>
          <Insight>{descriptionForContext(context)} The same selection is used when creating a new AI session, so the chat and overview stay aligned.</Insight>
        </DetailCard>
      </DetailGrid>

      <DetailCard>
        <Eyebrow>AI Questions</Eyebrow>
        <SectionTitle>Recent sessions</SectionTitle>
        {recentSessions.length > 0 ? (
          <SessionList>
            {recentSessions.map(session => (
              <SessionRow key={session.id}>
                <SessionText>
                  <SessionName title={sessionTitle(session)}>{sessionTitle(session)}</SessionName>
                  <SessionMeta title={sessionContextLine(session)}>{sessionContextLine(session)}</SessionMeta>
                </SessionText>
                <StatusPill $ok={!session.parentID}>{session.parentID ? 'Child' : 'Primary'}</StatusPill>
              </SessionRow>
            ))}
          </SessionList>
        ) : (
          <EmptyState>No AI Questions yet. Open the AI Questions tab and create the first session.</EmptyState>
        )}
      </DetailCard>
    </Wrap>
  );
}
